import { createContext, useState, useContext, ReactNode, useRef } from "react";
import { DEFAULT_TILE } from "../global/constants/tiles";
import { mockLayers } from "../global/constants/layers";
import { Geometry } from "geojson";
import { RasterValueRange } from "../global/constants/rasterValueFilters";
import { CONJ_DGC_LAYER_NAME } from "../global/constants/dgcConjuntos";

const DISTRIBUTION_SUBSTATION_LAYER_NAME = "neoenergia_pe_untrat";

interface ILayersContext {
  addNewLayer(layer: string[]): void;
  layers: string[];
  addNewTile(tile: string): void;
  tile: string;
  cleanCheckedLayers(): void;
  onSocioambientalLayerAdded?: () => void;
  setOnSocioambientalLayerAdded?: (callback: () => void) => void;
  layerTimeValues: Record<string, string>;
  setLayerTime: (layerName: string, timeValue: string) => void;
  layerElevationValues: Record<string, number>;
  setLayerElevation: (layerName: string, elevationValue: number) => void;
  getActiveLayersWithMetadata: () => Array<{ name: string; category: string }>;
  selectedGeometry: Geometry | null;
  setSelectedGeometry: (geometry: Geometry | null) => void;
  // CQL filters: map of layerName → selected voltage values (empty = show all)
  layerCqlFilters: Record<string, (number | string)[]>;
  setLayerVoltageFilter: (
    layerName: string,
    values: (number | string)[]
  ) => void;
  setOnAplLayerAdded?: (callback: () => void) => void;
  setOnSgbLayerAdded?: (callback: () => void) => void;
  setOnDgcLayerAdded?: (callback: () => void) => void;
  setOnDistributionSubstationLayerAdded?: (callback: () => void) => void;
  // Faixa de valor de pixel por raster (ausente = raster exibido inteiro)
  layerValueRanges: Record<string, RasterValueRange>;
  setLayerValueRange: (
    layerName: string,
    range: RasterValueRange | null
  ) => void;
}

interface IPageProvider {
  children: ReactNode;
}

const LayersContext = createContext<ILayersContext>({} as ILayersContext);

export const LayersProvider = ({ children }: IPageProvider) => {
  const [layers, setLayers] = useState([]);
  const [tile, setTile] = useState(DEFAULT_TILE);
  const [layerTimeValues, setLayerTimeValues] = useState<
    Record<string, string>
  >({});
  const [layerElevationValues, setLayerElevationValues] = useState<
    Record<string, number>
  >({});
  const [layerCqlFilters, setLayerCqlFilters] = useState<
    Record<string, (number | string)[]>
  >({});
  const [layerValueRanges, setLayerValueRanges] = useState<
    Record<string, RasterValueRange>
  >({});
  const socioambientalCallbackRef = useRef<(() => void) | null>(null);
  const aplCallbackRef = useRef<(() => void) | null>(null);
  const sgbCallbackRef = useRef<(() => void) | null>(null);
  const dgcCallbackRef = useRef<(() => void) | null>(null);
  const distributionSubstationCallbackRef = useRef<(() => void) | null>(null);
  const [selectedGeometry, setSelectedGeometry] = useState<Geometry | null>(
    null
  );

  //@ts-ignore
  function addNewLayer(newLayers) {
    const isRaster = (title: string) =>
      mockLayers.some((l) => l.title === title && l.type === "raster");

    const incomingRasters = newLayers.filter(isRaster);
    if (incomingRasters.length > 1) {
      const oldRasters = new Set<string>(layers.filter(isRaster));
      const newlyAddedRasters = incomingRasters.filter(
        (r: string) => !oldRasters.has(r)
      );
      const toKeep = new Set<string>([
        newlyAddedRasters.length > 0
          ? newlyAddedRasters[0]
          : incomingRasters[incomingRasters.length - 1],
      ]);
      newLayers = newLayers.filter(
        (l: string) => !isRaster(l) || toKeep.has(l)
      );
    }

    //@ts-ignore
    const newlyAddedLayers = newLayers.filter((l) => !layers.includes(l));

    if (newlyAddedLayers.length > 0) {
      // Fatores Socioambientais layer callback
      const hasSocioambientalLayer = newlyAddedLayers.some(
        (layerTitle: string) => {
          const layer = mockLayers.find((l) => l.title === layerTitle);
          return layer?.category === "Fatores Socioambientais";
        }
      );

      if (hasSocioambientalLayer && socioambientalCallbackRef.current) {
        socioambientalCallbackRef.current();
      }

      // APL layer callback
      const hasAplLayer = newlyAddedLayers.some((layerTitle: string) => {
        const layer = mockLayers.find((l) => l.title === layerTitle);
        return layer?.category === "Arranjos Produtivos Locais";
      });

      if (hasAplLayer && aplCallbackRef.current) {
        aplCallbackRef.current();
      }

      // SGB layer callback
      const hasSgbLayer = newlyAddedLayers.some((layerTitle: string) => {
        const layer = mockLayers.find((l) => l.title === layerTitle);
        return layer?.category === "Serviços Geológicos do Brasil (SGB)";
      });

      if (hasSgbLayer && sgbCallbackRef.current) {
        sgbCallbackRef.current();
      }

      // Conjuntos Elétricos - DGC layer callback
      const hasDgcLayer = newlyAddedLayers.some((layerTitle: string) => {
        const layer = mockLayers.find((l) => l.title === layerTitle);
        return layer?.layerName === CONJ_DGC_LAYER_NAME;
      });

      if (hasDgcLayer && dgcCallbackRef.current) {
        dgcCallbackRef.current();
      }

      // Subestações da rede de distribuição (Neoenergia) layer callback
      const hasDistributionSubstationLayer = newlyAddedLayers.some(
        (layerTitle: string) => {
          const layer = mockLayers.find((l) => l.title === layerTitle);
          return layer?.layerName === DISTRIBUTION_SUBSTATION_LAYER_NAME;
        }
      );

      if (
        hasDistributionSubstationLayer &&
        distributionSubstationCallbackRef.current
      ) {
        distributionSubstationCallbackRef.current();
      }
    }

    setLayers(newLayers);
    const activeRasterLayerNames = new Set<string>();
    newLayers.forEach((title: string) => {
      const layer = mockLayers.find((candidate) => candidate.title === title);
      if (layer?.type === "raster") {
        activeRasterLayerNames.add(layer.layerName);
      }
    });

    setLayerValueRanges((prev) => {
      const next = Object.fromEntries(
        Object.entries(prev).filter(([layerName]) =>
          activeRasterLayerNames.has(layerName)
        )
      );
      return Object.keys(next).length === Object.keys(prev).length
        ? prev
        : next;
    });
  }

  function setOnSocioambientalLayerAdded(callback: () => void) {
    socioambientalCallbackRef.current = callback;
  }

  //@ts-ignore
  function addNewTile(tile) {
    setTile(tile);
  }

  function cleanCheckedLayers() {
    setLayers([]);
    setLayerValueRanges({});
  }

  function setLayerTime(layerName: string, timeValue: string) {
    setLayerTimeValues((prev) => ({
      ...prev,
      [layerName]: timeValue,
    }));
  }

  function setLayerElevation(layerName: string, elevationValue: number) {
    setLayerElevationValues((prev) => ({
      ...prev,
      [layerName]: elevationValue,
    }));
  }

  function setLayerVoltageFilter(
    layerName: string,
    values: (number | string)[]
  ) {
    setLayerCqlFilters((prev) => ({
      ...prev,
      [layerName]: values,
    }));
  }

  function setLayerValueRange(
    layerName: string,
    range: RasterValueRange | null
  ) {
    setLayerValueRanges((prev) => {
      if (range === null) {
        if (!(layerName in prev)) return prev;
        const { [layerName]: _removed, ...rest } = prev;
        return rest;
      }
      return { ...prev, [layerName]: range };
    });
  }

  function getActiveLayersWithMetadata() {
    return layers.map((layerTitle: string) => {
      const layer = mockLayers.find((l) => l.title === layerTitle);
      return {
        name: layerTitle,
        category: layer?.category || "Unknown",
      };
    });
  }

  const setOnAplLayerAdded = (callback: () => void) => {
    aplCallbackRef.current = callback;
  };

  const setOnSgbLayerAdded = (callback: () => void) => {
    sgbCallbackRef.current = callback;
  };

  const setOnDgcLayerAdded = (callback: () => void) => {
    dgcCallbackRef.current = callback;
  };

  const setOnDistributionSubstationLayerAdded = (callback: () => void) => {
    distributionSubstationCallbackRef.current = callback;
  };

  return (
    <LayersContext.Provider
      value={{
        addNewLayer,
        layers,
        tile,
        addNewTile,
        cleanCheckedLayers,
        setOnSocioambientalLayerAdded,
        layerTimeValues,
        setLayerTime,
        layerElevationValues,
        setLayerElevation,
        getActiveLayersWithMetadata,
        selectedGeometry,
        setSelectedGeometry,
        layerCqlFilters,
        setLayerVoltageFilter,
        setOnAplLayerAdded,
        setOnSgbLayerAdded,
        setOnDgcLayerAdded,
        setOnDistributionSubstationLayerAdded,
        layerValueRanges,
        setLayerValueRange,
      }}
    >
      {children}
    </LayersContext.Provider>
  );
};

export function useLayers() {
  const context = useContext(LayersContext);

  if (!context) {
    throw new Error("usePageContext must be used within a PageProvider");
  }

  return context;
}
